import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRepeat,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  CodeSlash,
  Database,
  ExclamationTriangle,
  Folder2Open,
  FolderFill,
  HandThumbsUp,
  Laptop,
  Palette,
  PencilSquare,
  RocketTakeoff,
  ShieldLock,
  Speedometer2,
  Translate,
  TrashFill,
  UniversalAccess,
} from 'react-bootstrap-icons';
import type { Icon } from 'react-bootstrap-icons';

import { RequirementTestFolderNode } from '../../types/requirements';

interface TestDirectoryTreeProps {
  directory?: RequirementTestFolderNode | null;
  selectedFolderId?: string | null;
  highlightedFolderIds?: ReadonlySet<string>;
  onSelectFolder: (folder: RequirementTestFolderNode) => void;
  onRenameFolder?: (folder: RequirementTestFolderNode) => void;
  onDeleteFolder?: (folder: RequirementTestFolderNode) => void;
}

const DOUBLE_TAP_MS = 350;

type DirectoryIconTone =
  | 'root'
  | 'functional'
  | 'uiux'
  | 'usability'
  | 'compatibility'
  | 'performance'
  | 'security'
  | 'api'
  | 'database'
  | 'regression'
  | 'smoke'
  | 'accessibility'
  | 'localization'
  | 'deployment'
  | 'negative'
  | 'custom';

interface DirectoryIconConfig {
  Icon: Icon;
  tone: DirectoryIconTone;
  label: string;
}

const QA_CATEGORY_ICONS: Record<string, DirectoryIconConfig> = {
  'Functional Testing': { Icon: ClipboardCheck, tone: 'functional', label: 'Functional testing folder' },
  'UI/UX Testing': { Icon: Palette, tone: 'uiux', label: 'UI/UX testing folder' },
  'Usability Testing': { Icon: HandThumbsUp, tone: 'usability', label: 'Usability testing folder' },
  'Compatibility Testing': { Icon: Laptop, tone: 'compatibility', label: 'Compatibility testing folder' },
  'Performance Testing': { Icon: Speedometer2, tone: 'performance', label: 'Performance testing folder' },
  'Security Testing': { Icon: ShieldLock, tone: 'security', label: 'Security testing folder' },
  'API Testing': { Icon: CodeSlash, tone: 'api', label: 'API testing folder' },
  'Database Testing': { Icon: Database, tone: 'database', label: 'Database testing folder' },
  'Regression Testing': { Icon: ArrowRepeat, tone: 'regression', label: 'Regression testing folder' },
  'Smoke & Sanity Testing': { Icon: CheckCircle, tone: 'smoke', label: 'Smoke and sanity testing folder' },
  'Accessibility Testing': { Icon: UniversalAccess, tone: 'accessibility', label: 'Accessibility testing folder' },
  'Localization/Internationalization Testing': {
    Icon: Translate,
    tone: 'localization',
    label: 'Localization and internationalization testing folder',
  },
  'Installation/Deployment Testing': { Icon: RocketTakeoff, tone: 'deployment', label: 'Installation and deployment testing folder' },
  'Negative Testing': { Icon: ExclamationTriangle, tone: 'negative', label: 'Negative testing folder' },
};

const LEGACY_CATEGORY_ALIASES: Record<string, string> = {
  Accessibility: 'Accessibility Testing',
  API: 'API Testing',
  Compatibility: 'Compatibility Testing',
  Functional: 'Functional Testing',
  Negative: 'Negative Testing',
  Performance: 'Performance Testing',
  Regression: 'Regression Testing',
  Security: 'Security Testing',
  Smoke: 'Smoke & Sanity Testing',
};

function getDirectoryIconConfig(node: RequirementTestFolderNode, isExpanded: boolean): DirectoryIconConfig {
  const path = node.path || [];
  if (path.length <= 1) {
    return {
      Icon: isExpanded ? Folder2Open : FolderFill,
      tone: 'root',
      label: 'Root test directory',
    };
  }

  const topLevelName = path[1] || node.name;
  const categoryName = QA_CATEGORY_ICONS[topLevelName]
    ? topLevelName
    : LEGACY_CATEGORY_ALIASES[topLevelName];
  const categoryIcon = categoryName ? QA_CATEGORY_ICONS[categoryName] : undefined;
  if (categoryIcon) {
    return {
      ...categoryIcon,
      label: path.length === 2 ? categoryIcon.label : `${node.name} subfolder`,
    };
  }

  return {
    Icon: isExpanded ? Folder2Open : FolderFill,
    tone: 'custom',
    label: 'Custom folder',
  };
}

function buildDefaultExpanded(directory?: RequirementTestFolderNode | null): Record<string, boolean> {
  if (!directory) {
    return {};
  }

  const expanded: Record<string, boolean> = {
    [directory.id]: true,
  };
  directory.children.forEach((child) => {
    expanded[child.id] = true;
  });
  return expanded;
}

function TestDirectoryTree({
  directory,
  selectedFolderId,
  highlightedFolderIds,
  onSelectFolder,
  onRenameFolder,
  onDeleteFolder,
}: TestDirectoryTreeProps): React.ReactElement {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const lastTapRef = useRef<{ folderId: string; at: number } | null>(null);

  useEffect(() => {
    setExpanded(buildDefaultExpanded(directory));
  }, [directory]);

  function handleToggle(folderId: string): void {
    setExpanded((previous) => ({
      ...previous,
      [folderId]: !previous[folderId],
    }));
  }

  function canRename(node: RequirementTestFolderNode): boolean {
    return Boolean(onRenameFolder) && (node.path || []).length > 1;
  }

  function canDelete(node: RequirementTestFolderNode): boolean {
    return Boolean(onDeleteFolder) && (node.path || []).length > 1;
  }

  function handleDoubleClick(node: RequirementTestFolderNode): void {
    if (canRename(node)) {
      onRenameFolder?.(node);
    }
  }

  function handleTouchEnd(event: React.TouchEvent, node: RequirementTestFolderNode): void {
    if (!canRename(node)) {
      return;
    }

    const now = Date.now();
    const previous = lastTapRef.current;
    if (previous?.folderId === node.id && now - previous.at <= DOUBLE_TAP_MS) {
      event.preventDefault();
      lastTapRef.current = null;
      onRenameFolder?.(node);
      return;
    }
    lastTapRef.current = { folderId: node.id, at: now };
  }

  function renderNode(node: RequirementTestFolderNode, depth = 0): React.ReactNode {
    const isSelected = node.id === selectedFolderId;
    const isHighlighted = highlightedFolderIds?.has(node.id) ?? false;
    const isExpanded = expanded[node.id] ?? depth < 2;
    const hasChildren = node.children.length > 0;
    const iconConfig = getDirectoryIconConfig(node, isExpanded);
    const DirectoryIcon = iconConfig.Icon;

    return (
      <div key={node.id} className="rrw-tree-node">
        <div
          className={`rrw-tree-row ${isSelected ? 'rrw-tree-row-selected' : ''} ${isHighlighted ? 'rrw-tree-row-highlighted' : ''}`}
          style={{ paddingLeft: `${12 + depth * 14}px` }}
        >
          <button
            type="button"
            className="rrw-tree-toggle"
            onClick={() => handleToggle(node.id)}
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          >
            {hasChildren ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span />}
          </button>

          <button
            type="button"
            className="rrw-tree-select"
            onClick={() => onSelectFolder(node)}
            onDoubleClick={() => handleDoubleClick(node)}
            onTouchEnd={(event) => handleTouchEnd(event, node)}
          >
            <span
              className={`rrw-tree-icon rrw-tree-icon-${iconConfig.tone}`}
              title={iconConfig.label}
              aria-hidden="true"
            >
              <DirectoryIcon size={13} />
            </span>
            <span className="rrw-tree-name">{node.name}</span>
            <span className="rrw-tree-count rrw-tree-count-total" title={`${node.counts.total} test case${node.counts.total === 1 ? '' : 's'}`}>
              {node.counts.total}
            </span>
          </button>

          {canRename(node) && (
            <button
              type="button"
              className="rrw-tree-action rrw-tree-rename"
              onClick={(event) => {
                event.stopPropagation();
                onRenameFolder?.(node);
              }}
              aria-label={`Rename folder ${node.name}`}
              title="Rename folder"
            >
              <PencilSquare size={13} />
            </button>
          )}
          {canDelete(node) && (
            <button
              type="button"
              className="rrw-tree-action rrw-tree-delete"
              onClick={(event) => {
                event.stopPropagation();
                onDeleteFolder?.(node);
              }}
              aria-label={`Delete folder ${node.name}`}
              title="Delete folder"
            >
              <TrashFill size={13} />
            </button>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="rrw-tree-children">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  if (!directory) {
    return (
      <div className="rrw-empty-panel">
        No test directory is available for this job.
      </div>
    );
  }

  return (
    <div className="rrw-tree">
      {renderNode(directory)}
    </div>
  );
}

export default TestDirectoryTree;
